import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import {
  HomeIcon,
  UsersIcon,
  CubeIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  BanknotesIcon,
  LinkIcon,
  NewspaperIcon,
  PhotoIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  MegaphoneIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  QueueListIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  adminOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: "Overview",
    items: [
      { name: "Dashboard", href: "/", icon: HomeIcon, adminOnly: true },
      { name: "Statistiken", href: "/stats", icon: ChartBarIcon, adminOnly: true },
    ],
  },
  {
    title: "Verwaltung",
    items: [
      { name: "Claimants", href: "/claimants", icon: UsersIcon, adminOnly: true },
      { name: "Products", href: "/products", icon: CubeIcon, adminOnly: true },
      { name: "Bank Transfers", href: "/exports", icon: BanknotesIcon },
    ],
  },
  {
    title: "CMS - Content",
    items: [
      { name: "Headlines", href: "/headlines", icon: MegaphoneIcon, adminOnly: true },
      { name: "Section Content", href: "/section-content", icon: QueueListIcon, adminOnly: true },
      { name: "Media", href: "/media", icon: PhotoIcon, adminOnly: true },
      { name: "Testimonials", href: "/testimonials", icon: ChatBubbleLeftRightIcon, adminOnly: true },
      { name: "FAQ", href: "/faq", icon: QuestionMarkCircleIcon, adminOnly: true },
      { name: "Aktuelles", href: "/updates", icon: NewspaperIcon, adminOnly: true },
    ],
  },
  {
    title: "CMS - Seitenaufbau",
    items: [
      { name: "Landing Pages", href: "/landing-pages", icon: GlobeAltIcon, adminOnly: true },
      { name: "Site Stats", href: "/site-stats", icon: ChartBarIcon, adminOnly: true },
      { name: "Rechtliches", href: "/legal", icon: DocumentTextIcon, adminOnly: true },
    ],
  },
  {
    title: "System",
    items: [
      { name: "Surveys", href: "/surveys", icon: ClipboardDocumentListIcon, adminOnly: true },
      { name: "Einstellungen", href: "/settings", icon: Cog6ToothIcon, adminOnly: true },
      { name: "External Tools", href: "/links", icon: LinkIcon },
    ],
  },
];

export default function Layout() {
  const logout = useAuthStore((state) => state.logout);
  const role = useAuthStore((state) => state.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = role === "admin" || role === "super_admin";

  const isActiveLink = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-screen">
      {/* Logo */}
      <div className="flex-shrink-0 flex items-center h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
          ClaroClaim Admin
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 min-h-0">
        {navigation.map((section) => {
          const visibleItems = section.items.filter(
            (item) => !item.adminOnly || isAdmin
          );
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className="mb-6">
              <h3 className="px-4 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              <div className="space-y-1 px-2">
                {visibleItems.map((item) => {
                  const isActive = isActiveLink(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 ${
                          isActive
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-gray-400 dark:text-gray-500"
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Logout Button */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="absolute top-0 right-0 -mr-12 pt-2">
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          >
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
        </div>
        <SidebarContent />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex items-center h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 lg:hidden px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <span className="ml-4 text-lg font-semibold text-indigo-600 dark:text-indigo-400">
            ClaroClaim Admin
          </span>
        </div>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
